import { useState } from "react";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import LeadForm from "@/components/energy/LeadForm";
import { ToolShell, NumInput, TextInput, ErrorBox, ResultGrid, EstimateNote, sle } from "@/components/energy/tool";
import { api, formatApiError } from "@/lib/solarApi";

export default function EnergyOutage() {
  const [form, setForm] = useState({ outage_hours_per_day: "", outage_days_per_month: "", business_type: "", revenue_per_hour_sle: "", equipment_affected: "", spoiled_goods_monthly_sle: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showLead, setShowLead] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const compute = async () => {
    if (!form.outage_hours_per_day || !form.outage_days_per_month) {
      setError("Please enter outage hours per day and outage days per month.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/energy/outage", { ...form, spoiled_goods_monthly_sle: form.spoiled_goods_monthly_sle || 0, revenue_per_hour_sle: form.revenue_per_hour_sle || 0 });
      setResult(data.result);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo title="Power Outage Impact Calculator — BNM" description="Estimate what power outages cost your business in Sierra Leone — monthly and annually — and how backup energy could help." />
      <ToolShell
        testid="outage-tool"
        eyebrow="Power Outage Impact Calculator"
        title="What do outages cost your business?"
        lead="Estimate the productive time and revenue affected by unreliable power. Figures are estimates — not guaranteed savings."
      >
        <div className="rounded-lg border border-slate-200 bg-white p-6 sm:p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <NumInput label="Average outage hours per day" suffix="h" min={0} max={24} testid="out-hours" value={form.outage_hours_per_day} onChange={set("outage_hours_per_day")} />
            <NumInput label="Outage days per month" min={0} max={31} step={1} testid="out-days" value={form.outage_days_per_month} onChange={set("outage_days_per_month")} />
            <TextInput label="Business type" testid="out-type" value={form.business_type} onChange={set("business_type")} />
            <NumInput label="Estimated revenue per operating hour" suffix="SLE" min={0} testid="out-revenue" value={form.revenue_per_hour_sle} onChange={set("revenue_per_hour_sle")} />
            <TextInput label="Equipment affected by outages" testid="out-equipment" value={form.equipment_affected} onChange={set("equipment_affected")} />
            <NumInput label="Spoiled/damaged goods per month (if any)" suffix="SLE" min={0} testid="out-spoiled" value={form.spoiled_goods_monthly_sle} onChange={set("spoiled_goods_monthly_sle")} />
          </div>
          {error && <div className="mt-5"><ErrorBox msg={error} /></div>}
          <button onClick={compute} disabled={busy} data-testid="out-compute"
            className="mt-7 inline-flex items-center gap-2 rounded-md bg-obsidian px-7 py-3.5 font-semibold text-white hover:bg-pine hover:text-volt transition-colors disabled:opacity-60">
            {busy ? "Calculating…" : "Estimate My Impact"} <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        {result && (
          <div className="mt-10 space-y-8" data-testid="outage-results">
            <ResultGrid items={[
              { label: "Productive hours affected / month", value: result.productive_hours_lost_monthly },
              { label: "Estimated monthly impact", value: sle(result.monthly_impact_sle) },
              { label: "Estimated annual impact", value: sle(result.annual_impact_sle), hint: `≈ $${result.annual_impact_usd.toLocaleString()}` },
            ]} />
            <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-6">
              <h3 className="font-display text-lg font-bold text-slate-900">How backup energy helps</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                A right-sized solar + battery system keeps critical loads running through outages — refrigeration, lighting,
                tills, routers and equipment that protects your revenue. BNM designs backup systems around exactly the loads
                that matter most to your business.
              </p>
            </div>
            <EstimateNote />
            <button onClick={() => setShowLead(true)} data-testid="outage-cta"
              className="rounded-md bg-obsidian px-7 py-3.5 font-semibold text-white hover:bg-pine hover:text-volt transition-colors">
              Get a BNM Energy Assessment
            </button>
            {showLead && <LeadForm type="assessment" title="Get a BNM Energy Assessment" context={{ monthly_impact_sle: result.monthly_impact_sle, business: form.business_type }} defaultMessage={`Outages affect my ${form.business_type || "business"} about ${result.productive_hours_lost_monthly} hours/month.`} />}
          </div>
        )}
      </ToolShell>
    </>
  );
}
